import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CareerTimeline from './components/CareerTimeline';
import TrustBadges from './components/TrustBadges';
import Zertifikate from './components/Zertifikate';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function UeberMich() {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  usePageMeta({ canonical: '/ueber-mich' });

  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Jenny',
      jobTitle: 'Zahnmedizinische Prophylaxe-Assistentin (ZMP)',
      description: 'Inhaberin von Flow & Glow – Boutique-Studio für kosmetische Zahnaufhellung in Köln. Über 19 Jahre Erfahrung in der Dentalbranche.',
      url: `${siteUrl || 'https://flowundglow.de'}/ueber-mich`,
      image: `${siteUrl || 'https://flowundglow.de'}/jenny-portrait.jpg`,
      knowsAbout: [
        'Kosmetische Zahnaufhellung',
        'PAP-Technologie',
        'Zahnmedizinische Prophylaxe',
        'Peroxidfreie Zahnaufhellung',
        'Kosmetische Anwendungen',
      ],
      hasCredential: [
        {
          '@type': 'EducationalOccupationalCredential',
          name: 'Zahnmedizinische Prophylaxe-Assistentin (ZMP)',
          credentialCategory: 'degree',
          recognizedBy: {
            '@type': 'Organization',
            name: 'Landeszahnärztekammer Nordrhein',
          },
          dateCreated: '2007',
        },
        {
          '@type': 'EducationalOccupationalCredential',
          name: 'PAP-Technologie Zertifizierung',
          credentialCategory: 'certificate',
          recognizedBy: {
            '@type': 'Organization',
            name: 'Europäisches Kosmetikinstitut',
          },
          dateCreated: '2023',
        },
        {
          '@type': 'EducationalOccupationalCredential',
          name: 'Erste Hilfe & Hygieneschulung',
          credentialCategory: 'certificate',
          recognizedBy: {
            '@type': 'Organization',
            name: 'DRK Köln',
          },
          dateCreated: '2025',
        },
      ],
      alumniOf: [
        {
          '@type': 'EducationalOrganization',
          name: 'Landeszahnärztekammer Nordrhein',
          address: { '@type': 'PostalAddress', addressLocality: 'Düsseldorf', addressCountry: 'DE' },
        },
      ],
      worksFor: {
        '@type': 'HealthAndBeautyBusiness',
        name: 'Flow & Glow',
        url: siteUrl || 'https://flowundglow.de',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Köln',
          addressCountry: 'DE',
        },
      },
      sameAs: ['https://www.instagram.com/flowundglow'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Über mich – Jenny, Inhaberin Flow & Glow Köln',
      url: `${siteUrl || 'https://flowundglow.de'}/ueber-mich`,
      description: 'Lerne Jenny kennen – ZMP mit 19 Jahren Erfahrung und Gründerin von Flow & Glow, dem Boutique-Studio für kosmetische Zahnaufhellung in Köln.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Startseite', item: siteUrl || 'https://flowundglow.de' },
          { '@type': 'ListItem', position: 2, name: 'Über mich', item: `${siteUrl || 'https://flowundglow.de'}/ueber-mich` },
        ],
      },
    },
  ]);

  return (
    <main className="font-sans">
      <Navbar />

      {/* Hero */}
      <section className="overflow-hidden">

        {/* ─── DESKTOP: Split-screen ─── */}
        <div className="hidden lg:flex relative min-h-[90vh] items-stretch">
          <div className="absolute inset-0 flex">
            {/* Left: Photo */}
            <div className="w-[42%] relative">
              <img
                src="https://static.readdy.ai/image/ca60b0043e5ac75b0711db9039bc28de/916ac9d323bd869a59918b36c30dfb51.webp"
                alt="Jenny – Inhaberin Flow & Glow Köln"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-charcoal/30" />
            </div>
            {/* Right: Charcoal bg */}
            <div className="w-[58%] bg-charcoal" />
          </div>
          <div className="relative z-10 w-full flex items-center">
            <div className="max-w-7xl mx-auto px-8 w-full pt-28 pb-16">
              <div className="ml-[42%] pl-12 max-w-xl">
                <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
                  Meine Geschichte
                </span>
                <h1 className="font-serif text-5xl lg:text-6xl font-medium text-white mt-4 mb-6 leading-tight">
                  Seit ich 16 bin, dreht sich mein Leben um Zähne und Lächeln.
                </h1>
                <p className="font-sans text-base text-white/70 leading-relaxed mb-8">
                  Was als Faszination begann, wurde zu meiner Berufung. Nach 19 Jahren in der Dentalbranche habe ich Flow &amp; Glow gegründet — mein eigenes Boutique-Studio für kosmetische Zahnaufhellung, wo alle die zu mir kommen das bekommen, was sie verdienen: echte Expertise, warmherzige Betreuung und ein strahlendes Ergebnis.
                </p>
                <div className="grid grid-cols-3 gap-6 mb-8 py-6 border-y border-white/15">
                  {[
                    { value: '19+', label: 'Jahre Erfahrung' },
                    { value: '100+', label: 'Gäste' },
                    { value: '3', label: 'Anwendungen' },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <div className="font-serif text-4xl font-medium text-rose mb-1">{s.value}</div>
                      <div className="font-sans text-xs text-white/50">{s.label}</div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/leistungen"
                  className="inline-flex items-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Meine Leistungen entdecken
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ─── MOBILE: Stacked ─── */}
        <div className="lg:hidden flex flex-col">
          {/* Image – fully visible, goes behind the fixed navbar */}
          <div className="relative w-full" style={{ height: '58vmax' }}>
            <img
              src="https://static.readdy.ai/image/ca60b0043e5ac75b0711db9039bc28de/916ac9d323bd869a59918b36c30dfb51.webp"
              alt="Jenny – Inhaberin Flow & Glow Köln"
              className="w-full h-full object-cover object-top"
            />
            {/* Very light gradient only at the very bottom edge for transition */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-charcoal" />
          </div>

          {/* Content on solid charcoal */}
          <div className="bg-charcoal px-5 pb-14 pt-2">
            <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
              Meine Geschichte
            </span>
            <h1 className="font-serif text-3xl font-medium text-white mt-3 mb-5 leading-tight">
              Seit ich 16 bin, dreht sich mein Leben um Zähne und Lächeln.
            </h1>
            <p className="font-sans text-sm text-white/70 leading-relaxed mb-7">
              Was als Faszination begann, wurde zu meiner Berufung. Nach 19 Jahren in der Dentalbranche habe ich Flow &amp; Glow gegründet — mein eigenes Boutique-Studio für kosmetische Zahnaufhellung.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-7 py-5 border-y border-white/15">
              {[
                { value: '19+', label: 'Jahre Erfahrung' },
                { value: '100+', label: 'Gäste' },
                { value: '3', label: 'Anwendungen' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-serif text-3xl font-medium text-rose mb-1">{s.value}</div>
                  <div className="font-sans text-xs text-white/50">{s.label}</div>
                </div>
              ))}
            </div>

            <Link
              to="/leistungen"
              className="inline-flex items-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-sm px-6 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Meine Leistungen entdecken
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </Link>
          </div>
        </div>

      </section>

      {/* My story section */}
      <section className="bg-blush py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-1/2">
              <span className="font-sans text-xs tracking-widest uppercase text-rose font-semibold">
                Warum kosmetische Zahnaufhellung?
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal mt-3 mb-6 leading-tight">
                Kosmetische Expertise mit Dental-Hintergrund
              </h2>
              <p className="font-sans text-base text-charcoal/70 leading-relaxed">
                Jenny ist Zahnmedizinische Prophylaxeassistentin mit 19 Jahren Erfahrung in der Dentalbranche. Bei Flow &amp; Glow nutzt sie ihre fundierte Expertise, um eine professionelle, kosmetische Zahnaufhellung in einer wohlfühlenden Boutique-Atmosphäre anzubieten — als Ergänzung zur regelmäßigen zahnärztlichen Vorsorge.
              </p>
            </div>
            <div className="lg:w-1/2">
              <p className="font-sans text-base text-charcoal/70 leading-relaxed mb-5">
                Bei Flow &amp; Glow bekommst du kosmetische Zahnaufhellung in einer Atmosphäre, die sich anfühlt wie ein Besuch bei einer guten Freundin — warm, entspannt und ohne Stress. Jede Person ist hier willkommen. Flow &amp; Glow ist ein kosmetisches Studio und keine zahnärztliche Praxis.
              </p>
              <p className="font-sans text-base text-charcoal/70 leading-relaxed">
                Die PAP-Technologie, die ich verwende, ist peroxidfrei, schonend und dabei hochwirksam. Alle Anwendungen sind rein kosmetischer Natur und ergänzen die regelmäßige zahnärztliche Vorsorge.
              </p>

              {/* Quote */}
              <blockquote className="mt-8 pl-5 border-l-4 border-rose">
                <p className="font-serif text-lg italic text-charcoal/80 leading-relaxed">
                  "Dein Lächeln ist das erste, was andere sehen — und ich möchte, dass du es strahlend und selbstbewusst zeigst."
                </p>
                <footer className="mt-3 font-sans text-sm text-charcoal/60 font-medium">— Jenny, Inhaberin Flow &amp; Glow</footer>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Career timeline */}
      <CareerTimeline />

      {/* Zertifikate & Ausbildung */}
      <Zertifikate />

      {/* Trust badges */}
      <TrustBadges />

      {/* Final CTA */}
      <section className="bg-charcoal py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-5 leading-tight">
            Lern mich persönlich kennen
          </h2>
          <p className="font-sans text-base text-white/65 leading-relaxed mb-10 max-w-lg mx-auto">
            Ich freue mich darauf, dir und deinem Lächeln zu begegnen. Buche jetzt einen Termin in meinem Studio in Köln.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://simplybook.me"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line text-base" />
              </span>
              Termin buchen
            </a>
            <a
              href="https://wa.me/4922100000"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base" />
              </span>
              WhatsApp schreiben
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
